import { GridInputs, Table } from "../../../../../../components/Functional";
import { EXHIBITED_OPTIONS } from "../../../../../../constants/exhibitedOptions";
import { UPDATE_EXHIBITION_WORK } from "../../../../../../graphql/mutation/ExhibitionWork";
import { FETCH_EXHIBITION_WORKS_V2 as FETCH_EXHIBITION_WORKS } from "../../../../../../graphql/query/ExhibitionWork";
import useUser from "../../../../../../hooks/useUser";
import { formatDate } from "../../../../../../util";
import exhibitionTypeOptions from "../../../../exhibitionTypeOptions";
import { getExhibitionPresentationInput } from "../../../../exhibitionPresentation";
import SectionHeader from "../../components/SectionHeader";
import Actions from "./components/Actions";
import AddWork from "./components/AddWork";

const Infos = ({ disabled, formik, submitOnBlur = false }) => {
  const { hasPermission } = useUser();
  const inputs = {
    className: "my-2 grid w-full grid-cols-1 lg:grid-cols-2",
    inputs: [
      {
        className: "grid gap-4 p-4 w-[85%] xl:w-[70%] col-span-1",
        inputs: [
          {
            className: "grid grid-cols-1 gap-4",
            inputs: [
              {
                label: "Title",
                name: "title",
                type: "text",
                submitOnBlur,
                disabled,
              },
              {
                label: "Contact",
                name: "contactId",
                type: "contactSearch",
                redirect: true,
                submitOnBlur,
                disabled,
              },
              {
                label: "Company",
                name: "contact.company",
                type: "text",
                disabled: true,
              },
            ],
          },
          {
            className: "grid grid-cols-1 gap-4 sm:grid-cols-2",
            inputs: [
              {
                label: "Type",
                name: "type",
                type: "multi-select",
                options: exhibitionTypeOptions,
                submitOnBlur,
                disabled,
              },
              getExhibitionPresentationInput({
                disabled: !hasPermission(
                  "UPDATE_EXHIBITION_CHANNEL_PRESENTATION",
                ),
                submitOnBlur,
              }),
              {
                label: "Sage Code",
                name: "sageCode",
                type: "text",
                submitOnBlur,
                disabled,
              },
              {
                label: "Curator",
                name: "curator",
                type: "text",
                submitOnBlur,
                disabled,
              },
              {
                label: "Start Date",
                name: "startDate",
                type: "date",
                submitOnBlur,
                disabled,
                onInput: evt => {
                  const date = evt.target.value; // YYYY-MM-DD
                  const [startYear, startMonth, startDay] =
                    date?.split("-") ?? [];
                  formik.setFieldValue(
                    "startDay",
                    date ? parseInt(startDay) : null,
                  );
                  formik.setFieldValue(
                    "startMonth",
                    date ? parseInt(startMonth) : null,
                  );
                  formik.setFieldValue(
                    "startYear",
                    date ? parseInt(startYear) : null,
                  );
                },
              },
              {
                label: "End Date",
                name: "endDate",
                type: "date",
                submitOnBlur,
                disabled,
                onInput: evt => {
                  const date = evt.target.value; // YYYY-MM-DD
                  const [endYear, endMonth, endDay] = date?.split("-") ?? [];
                  formik.setFieldValue(
                    "endDay",
                    date ? parseInt(endDay) : null,
                  );
                  formik.setFieldValue(
                    "endMonth",
                    date ? parseInt(endMonth) : null,
                  );
                  formik.setFieldValue(
                    "endYear",
                    date ? parseInt(endYear) : null,
                  );
                },
              },
            ],
          },
        ],
      },
      {
        inputClassName: "p-4 w-[85%] lg:w-[70%]",
        label: "Notes",
        name: "notes",
        type: "textarea",
        rows: 20,
        submitOnBlur,
        disabled,
      },
    ],
  };

  return (
    <div className="grid gap-x-20 gap-y-4">
      <GridInputs {...inputs} formik={formik} />
    </div>
  );
};

const ExhibitionWorks = props => {
  const { exhibition, disabled } = props;
  const { hasPermission } = useUser();

  const tableProps = {
    variant: "dynamicHeight",
    dataKey: "exhibitionWorksV2",
    headers: [
      {
        label: "Ref",
        name: "instanceId",
        type: "input",
        inputType: "instanceSearch",
        emptySelectionLabel: "Select Instance",
        disabledIfFilled: true,
        disabled,
        redirect: true,
      },
      {
        label: "Title",
        type: "label",
        name: "instance.edition.artwork.title",
      },
      {
        label: "Edition",
        type: "custom",
        component: props => {
          const { values } = props;
          return <div>{values?.instance?.editionNumberFormatted}</div>;
        },
      },
      {
        label: "Type",
        type: "label",
        name: "instance.edition.artwork.artworkCategory.name",
      },
      {
        label: "Year",
        type: "label",
        name: "instance.edition.year",
      },
      {
        label: "Artist(s)",
        type: "label",
        name: "instance.artistNames",
      },
      {
        label: "Notes",
        type: "input",
        inputType: "long-text",
        name: "notes",
        disabled,
      },
      {
        label: "Position",
        type: "input",
        inputType: "multi-select",
        options: [
          { value: "wall", label: "Wall" },
          { value: "floor", label: "Floor" },
        ],
        name: "artworkCat",
        disabled,
      },
      {
        label: "Exhibited",
        type: "input",
        inputType: "multi-select",
        options: EXHIBITED_OPTIONS,
        name: "exhibited",
        disabled,
      },
      {
        label: "Technical Notes",
        type: "input",
        inputType: "long-text",
        name: "technicalNotes",
        disabled,
      },
      {
        label: "User",
        type: "label",
        name: "user",
      },
      {
        label: "Timestamp",
        type: "custom",
        component: props => {
          const { values } = props;

          return (
            <div title={values?.updatedAt}>{formatDate(values?.updatedAt)}</div>
          );
        },
      },
      {
        label: "",
        name: "action",
        type: "custom",
        component: Actions,
      },
    ],
    FETCH_QUERY: FETCH_EXHIBITION_WORKS(),
    UPDATE_MUTATION: UPDATE_EXHIBITION_WORK(),
    REFETCH_QUERIES: [],
    sequentialRowUpdates: true,
    variables: {
      _exhibition: { id: { operator: "eq", value: exhibition?.id } },
      removed: { operator: "eq", value: 0 },
    },
  };

  const actions = [
    {
      component: () => (
        <AddWork
          exhibition={exhibition}
          isPlaceholder
          disabled={!hasPermission("UPDATE_EXHIBITION_WORK")}
        />
      ),
    },
    {
      component: () => (
        <AddWork
          exhibition={exhibition}
          disabled={!hasPermission("UPDATE_EXHIBITION_WORK")}
        />
      ),
    },
  ];

  return (
    <div className="mt-6">
      <SectionHeader title="Artworks" actions={actions} />
      <div className="mb-6 w-full overflow-x-auto">
        <Table {...tableProps} />
      </div>
    </div>
  );
};

const ExhibitionDetails = props => {
  const { exhibition, formik } = props;
  const { hasPermission } = useUser();

  return (
    <div className="w-full pt-8">
      <div className="px-4 sm:px-6 lg:px-8">
        <Infos
          formik={formik}
          submitOnBlur={true}
          disabled={!hasPermission("UPDATE_EXHIBITION")}
        />
      </div>
      <ExhibitionWorks
        exhibition={exhibition}
        disabled={!hasPermission("UPDATE_EXHIBITION_WORK")}
      />
    </div>
  );
};

export default ExhibitionDetails;
